import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PRE_REGISTRATION_ENDPOINTS } from '@common/utils/api-endpoints.const';
import { PreRegistrationSubmitResponse } from '@common/interfaces/pre-registration/pre-registration.interface';

/**
 * Appends a (possibly nested) value to a FormData instance.
 * - File values are appended as-is (multipart).
 * - Nested objects are flattened as `key[child]` (bilingual { en, ar } included).
 * - null/undefined values are skipped.
 */
export function appendToFormData(
  formData: FormData,
  key: string,
  value: unknown
): void {
  if (value === null || value === undefined) return;

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (value instanceof Date) {
    formData.append(key, value.toISOString());
    return;
  }

  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([child, val]) =>
      appendToFormData(formData, `${key}[${child}]`, val)
    );
    return;
  }

  formData.append(key, String(value));
}

/** Builds a FormData body from a plain object (used for both validate and submit). */
export function buildFormData(source: Record<string, unknown>): FormData {
  const formData = new FormData();
  Object.entries(source).forEach(([key, value]) =>
    appendToFormData(formData, key, value)
  );
  return formData;
}

@Injectable({ providedIn: 'root' })
export class PreRegistrationActions {
  private readonly http = inject(HttpClient);

  /** Validates a single step's (partial) body before advancing. */
  validateStep(payload: Record<string, unknown>): Observable<unknown> {
    return this.http.post(
      PRE_REGISTRATION_ENDPOINTS.VALIDATE,
      buildFormData(payload)
    );
  }

  /** Submits the full pre-registration request as multipart form-data. */
  submit(payload: FormData): Observable<PreRegistrationSubmitResponse> {
    return this.http.post<PreRegistrationSubmitResponse>(
      PRE_REGISTRATION_ENDPOINTS.SUBMIT,
      payload
    );
  }
}
